import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react'
import { PageLoadContext } from '@core/providers/PageLoadContext'
import { getInstance } from '@core/utils'
import { joinApiPath, resolveApiRequest } from '@core/utils/apiRouting'
import { useParams } from 'react-router'

const useFetch = ({
  apiId,
  service,
  url = '',
  method = 'GET',
  isPublic = false,
  isMultipart,
  headers,
  baseURL = null,
  isBlob = false
}) => {
  const [pending, setPending] = useState(false)
  const [data, setData] = useState()
  const pageLoad = useContext(PageLoadContext)
  const pageRequests = useRef(new Set())
  const isMounted = useRef(true) // ใช้ useRef เพื่อติดตามสถานะของ component

  const param = useParams()

  const axiosInstance = React.useMemo(() => {
    return getInstance({ isPublic, redirectWith: param?.companyGroup, isBlob })
  }, [isPublic, param?.companyGroup, isBlob])

  // cleanup function ที่จะตั้งค่า isMounted เป็น false เมื่อ component unmount
  useEffect(() => {
    const requests = pageRequests.current
    return () => {
      isMounted.current = false // component unmount
      requests.forEach((finish) => finish())
      requests.clear()
    }
  }, [])

  const execute = useCallback(
    async (
      { url: postUrl, payload, headers: executeHeader },
      { onSuccess, onFailed, onFinally } = {}
    ) => {
      const finishPageLoad = pageLoad?.begin()
      if (finishPageLoad) pageRequests.current.add(finishPageLoad)
      if (isMounted.current) {
        setPending(true)
      }
      try {
        const parameter = {
          method,
          headers: {
            ...headers,
            ...executeHeader
          },
          ...(['GET', 'DELETE'].includes(method.toUpperCase())
            ? {
                params: {
                  ...payload,
                  ...(isPublic && { kc: param?.companyGroup.toUpperCase() })
                }
              }
            : {
                data: {
                  ...payload,
                  ...(isPublic && { kc: param?.companyGroup.toUpperCase() })
                }
              })
        }

        if (!isMultipart) {
          parameter.headers['Content-Type'] = 'application/json'
        } else {
          const rewriteForm = new FormData()
          if (payload.getAll('files').length > 0) {
            payload.getAll('files').forEach((file) => {
              rewriteForm.append('files', file)
            })
          }

          if (payload.get('file')) {
            rewriteForm.append('file', payload.get('file'))
          }

          for (const [key, value] of payload.entries()) {
            if (key !== 'files' && key !== 'file') {
              rewriteForm.append(key, value)
            }
          }

          parameter.data = rewriteForm
        }

        const target = baseURL
          ? {
              baseURL,
              url: joinApiPath(url, postUrl)
            }
          : resolveApiRequest({
              apiId,
              service,
              url,
              postUrl
            })

        const opt = {
          baseURL: target.baseURL,
          url: target.url,
          ...parameter
        }

        const response = await axiosInstance(opt)
        if (response) {
          let nextData = response
          if (onSuccess && isMounted.current) {
            nextData = await onSuccess(response)
          }
          if (isMounted.current) {
            // ตรวจสอบว่า component ยัง mount อยู่หรือไม่
            setData(nextData)
          }
        } else {
          throw new Error('No Response')
        }
      } catch (error) {
        console.error(error)

        if (onFailed && isMounted.current) {
          await onFailed(error)
        }
      } finally {
        try {
          if (onFinally && isMounted.current) {
            await onFinally()
          }
          if (isMounted.current) {
            // ตรวจสอบว่า component ยัง mount อยู่หรือไม่
            setPending(false)
          }
        } finally {
          finishPageLoad?.()
          pageRequests.current.delete(finishPageLoad)
        }
      }
    },
    [
      axiosInstance,
      pageLoad,
      headers,
      isMultipart,
      method,
      url,
      baseURL,
      apiId,
      service,
      isPublic,
      param?.companyGroup
    ]
  )

  return { execute, pending, data }
}

export default useFetch
